import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useAuth} from '@/hooks/useAuth';
import {motion, useInView, useMotionTemplate, useMotionValue} from 'framer-motion';
import {
	ArrowRight,
	Briefcase,
	Building2,
	CalendarDays,
	CheckCircle2,
	ChevronLeft,
	ChevronRight,
	Globe,
	GraduationCap,
	Heart,
	HelpCircle,
	Lightbulb,
	MapPin,
	MessageCircle,
	Monitor,
	Quote,
	Search,
	Send,
	Shield,
	ShoppingBag,
	Sparkles,
	Star,
	TrendingUp,
	Users,
	UtensilsCrossed,
	Wrench,
	X,
	Zap,
} from 'lucide-react';

const CATS = {
	SHOPPING_RETAIL: {icon: ShoppingBag, color: '#E8722A', bg: '#FFF7ED'},
	FOOD_BEVERAGES: {icon: UtensilsCrossed, color: '#DC2626', bg: '#FEF2F2'},
	BEAUTY_HEALTH: {icon: Heart, color: '#DB2777', bg: '#FDF2F8'},
	PROFESSIONAL_SERVICES: {icon: Briefcase, color: '#4F46E5', bg: '#EEF2FF'},
	EDUCATION_TRAINING: {icon: GraduationCap, color: '#7C3AED', bg: '#F5F3FF'},
	EVENTS_LEISURE: {icon: CalendarDays, color: '#0891B2', bg: '#ECFEFF'},
	TECHNOLOGY_IT: {icon: Monitor, color: '#0284C7', bg: '#F0F9FF'},
	CONSTRUCTION_HOME: {icon: Wrench, color: '#65A30D', bg: '#F7FEE7'},
	OTHER: {icon: Briefcase, color: '#6B7280', bg: '#F9FAFB'},
};
const FAQ_ICONS = [Zap, Lightbulb, Shield, MessageCircle, HelpCircle, Star];
const FAQ_COLORS = ['#E8722A', '#4F46E5', '#0891B2', '#7C3AED', '#DB2777', '#65A30D'];

// ═══════════════════════════════════════════════════════════════════════════
// DUMMY
// ═══════════════════════════════════════════════════════════════════════════
const D = {
	config: {
		hero: {
			heading: {en: 'Find Local Businesses in Our Community', mr: 'आमच्या समुदायातील स्थानिक व्यवसाय शोधा'},
			subHeading: {
				en: 'Support community businesses. Find trusted services from families you know.',
				mr: 'समुदायातील व्यवसायांना पाठिंबा द्या. ओळखीच्या कुटुंबांकडून विश्वसनीय सेवा शोधा.'
			},
			backgroundImageUrl: '/assets/yellow-page-hero.png'
		},
		sections: [{key: 'hero', visible: true}, {key: 'stats', visible: true}, {
			key: 'categories',
			visible: true
		}, {key: 'featured', visible: true}, {key: 'spotlight', visible: true}, {
			key: 'newest',
			visible: true
		}, {key: 'testimonials', visible: true}, {key: 'faq', visible: true}, {key: 'footerCta', visible: true}],
		spotlight: {
			quote: {
				en: 'Starting this business was my dream, and this community made it possible. JoglekarKul helped me connect with customers who feel like family.',
				mr: 'हा व्यवसाय सुरू करणे हे माझे स्वप्न होते आणि या समुदायाने ते शक्य केले.'
			},
			ownerName: 'Ramesh Joglekar',
			ownerRole: {en: 'Founder, Joglekar Electronics', mr: 'संस्थापक, जोगळेकर इलेक्ट्रॉनिक्स'},
			stats: [{label: {en: 'Years', mr: 'वर्षे'}, value: '15+'}, {
				label: {en: 'Customers', mr: 'ग्राहक'},
				value: '2K+'
			}, {label: {en: 'Rating', mr: 'रेटिंग'}, value: '4.8★'}],
			story: {
				en: "From a small repair shop to a thriving electronics business, Ramesh's journey is a testament to community support.",
				mr: 'एका छोट्या दुरुस्तीच्या दुकानापासून ते भरभराटीच्या व्यवसायापर्यंत.'
			}
		},
		footerCta: {
			heading: {en: 'Ready to grow your business?', mr: 'तुमचा व्यवसाय वाढवायला तयार आहात?'},
			description: {
				en: 'Join JoglekarKul Yellow Pages and reach thousands of potential customers in your community. Get listed today!',
				mr: 'जोगळेकरकुल यलो पेजेसमध्ये सामील व्हा आणि हजारो ग्राहकांपर्यंत पोहोचा.'
			}
		},
	},
	stats: {entityCount: 127, memberCount: 3450, categoryCount: 9},
	categories: [
		{_id: '1', slug: 'SHOPPING_RETAIL', name: {en: 'Shopping & Retail', mr: 'शॉपिंग अँड रिटेल'}},
		{_id: '2', slug: 'FOOD_BEVERAGES', name: {en: 'Food & Beverages', mr: 'फूड अँड बेव्हरेजेस'}},
		{_id: '3', slug: 'BEAUTY_HEALTH', name: {en: 'Beauty & Health', mr: 'ब्युटी अँड हेल्थ'}},
		{_id: '4', slug: 'PROFESSIONAL_SERVICES', name: {en: 'Professional Services', mr: 'प्रोफेशनल सर्व्हिसेस'}},
		{_id: '5', slug: 'EDUCATION_TRAINING', name: {en: 'Education & Training', mr: 'एज्युकेशन अँड ट्रेनिंग'}},
		{_id: '6', slug: 'EVENTS_LEISURE', name: {en: 'Events & Leisure', mr: 'इव्हेंट्स अँड लेजर'}},
		{_id: '7', slug: 'TECHNOLOGY_IT', name: {en: 'Technology & IT', mr: 'टेक्नॉलॉजी अँड आयटी'}},
		{_id: '8', slug: 'CONSTRUCTION_HOME', name: {en: 'Construction & Home', mr: 'कन्स्ट्रक्शन अँड होम'}},
	],
	featured: [
		{
			_id: 'f1',
			entityName: 'TechFix Solutions',
			categories: ['TECHNOLOGY_IT'],
			tagline: 'Professional repair for phones, laptops & gadgets. Quick turnaround guaranteed.',
			photos: {logo: {url: ''}},
			verification: {isVerified: true},
			rating: {average: 4.5, count: 89},
			isPhysical: true,
			location: 'Kothrud, Pune',
			listedBy: {displayName: 'Amit Joglekar'}
		},
		{
			_id: 'f2',
			entityName: 'Ethnic Threads',
			categories: ['SHOPPING_RETAIL'],
			tagline: 'Traditional & modern Maharashtrian attire for all occasions and celebrations.',
			photos: {logo: {url: ''}},
			verification: {isVerified: true},
			rating: {average: 4.8, count: 156},
			isPhysical: true,
			location: 'FC Road, Pune',
			listedBy: {displayName: 'Sneha Kulkarni'}
		},
		{
			_id: 'f3',
			entityName: 'Radiant Beauty',
			categories: ['BEAUTY_HEALTH'],
			tagline: 'Personalized beauty & wellness services delivered at your doorstep with care.',
			photos: {logo: {url: ''}},
			verification: {isVerified: false},
			rating: {average: 4.2, count: 34},
			isPhysical: false,
			serviceArea: 'Pune Metro',
			listedBy: {displayName: 'Priya Deshpande'}
		},
		{
			_id: 'f4',
			entityName: 'Patil Law Firm',
			categories: ['PROFESSIONAL_SERVICES'],
			tagline: 'Expert legal consultation for property, family, and business matters.',
			photos: {logo: {url: ''}},
			verification: {isVerified: true},
			rating: {average: 4.9, count: 72},
			isPhysical: true,
			location: 'Deccan, Pune',
			listedBy: {displayName: 'Adv. Rajesh Patil'}
		},
		{
			_id: 'f5',
			entityName: 'Saraswati Academy',
			categories: ['EDUCATION_TRAINING'],
			tagline: 'Coaching for competitive exams since 2010. Proven results, trusted faculty.',
			photos: {logo: {url: ''}},
			verification: {isVerified: true},
			rating: {average: 4.6, count: 210},
			isPhysical: true,
			location: 'Karve Nagar, Pune',
			listedBy: {displayName: 'Prof. Milind Joshi'}
		},
	],
	newest: [
		{
			_id: 'n1',
			entityName: 'Shreeji Sweets',
			categories: ['FOOD_BEVERAGES'],
			tagline: 'Finest Maharashtrian sweets & savories made with traditional recipes.',
			photos: {logo: {url: ''}},
			verification: {isVerified: false},
			rating: {average: 0, count: 0},
			isPhysical: true,
			location: 'Aundh, Pune',
			listedBy: {displayName: 'Mahesh Joshi'}
		},
		{
			_id: 'n2',
			entityName: 'Omkar Real Estate',
			categories: ['PROFESSIONAL_SERVICES'],
			tagline: 'Trusted property solutions since 2005. Residential & commercial.',
			photos: {logo: {url: ''}},
			verification: {isVerified: true},
			rating: {average: 4.6, count: 45},
			isPhysical: true,
			location: 'Baner, Pune',
			listedBy: {displayName: 'Omkar Bhosale'}
		},
		{
			_id: 'n3',
			entityName: 'Sai Packers & Movers',
			categories: ['OTHER'],
			tagline: 'Reliable relocation services across Maharashtra. Damage-free guarantee.',
			photos: {logo: {url: ''}},
			verification: {isVerified: false},
			rating: {average: 3.8, count: 12},
			isPhysical: false,
			serviceArea: 'Maharashtra',
			listedBy: {displayName: 'Vinay Sawant'}
		},
		{
			_id: 'n4',
			entityName: 'Ganesh Decorators',
			categories: ['EVENTS_LEISURE'],
			tagline: 'Wedding & festival decoration specialists. Making moments memorable.',
			photos: {logo: {url: ''}},
			verification: {isVerified: true},
			rating: {average: 4.4, count: 67},
			isPhysical: true,
			location: 'Shivaji Nagar, Pune',
			listedBy: {displayName: 'Ganesh More'}
		},
	],
	testimonials: [
		{
			_id: 't1',
			quote: 'JoglekarKul Yellow Pages has been a game-changer for finding local businesses. I discovered a fantastic catering service for my daughter\'s wedding!',
			author: 'Priya Kulkarni',
			authorTitle: 'Pune Member',
			rating: 5
		},
		{
			_id: 't2',
			quote: 'I found a reliable electrician through the directory. The community trust factor makes all the difference when choosing a service provider.',
			author: 'Arjun Marathe',
			authorTitle: 'Mumbai Member',
			rating: 4
		},
		{
			_id: 't3',
			quote: 'Listed my boutique here and got 15 new customers in the first month! The community support is incredible and heartwarming.',
			author: 'Sneha Deshpande',
			authorTitle: 'Nashik Member',
			rating: 5
		},
	],
	faqs: [
		{
			_id: 'fq1',
			question: {en: 'How do I list my business?', mr: 'मी माझा व्यवसाय कसा सूचीबद्ध करू?'},
			answer: {
				en: 'Tap the "+" button on the navigation bar, fill the quick form with your details, verify via OTP, and you\'re live in 2-3 minutes!',
				mr: 'नेव्हिगेशन बारवरील "+" बटणावर टॅप करा, तपशील भरा, OTP सत्यापित करा आणि २-३ मिनिटांत लाइव्ह!'
			}
		},
		{
			_id: 'fq2',
			question: {en: 'Is listing free?', mr: 'सूचीबद्ध करणे मोफत आहे का?'},
			answer: {
				en: 'Yes! Basic listing is completely free for all community members. We want every business to get discovered.',
				mr: 'होय! बेसिक लिस्टिंग सर्व सदस्यांसाठी पूर्णपणे मोफत आहे.'
			}
		},
		{
			_id: 'fq3',
			question: {en: 'How does verification work?', mr: 'सत्यापन कसे काम करते?'},
			answer: {
				en: 'We verify your WhatsApp number via OTP. Once verified, your listing gets a green "Verified" badge that builds trust with customers.',
				mr: 'आम्ही व्हॉट्सएप OTP द्वारे सत्यापित करतो. सत्यापित झाल्यावर हिरवा बॅज मिळतो.'
			}
		},
		{
			_id: 'fq4',
			question: {en: 'Can I have multiple listings?', mr: 'एकापेक्षा जास्त लिस्टिंग ठेवता येतील?'},
			answer: {
				en: 'Absolutely! Create separate listings for each business or service you offer from your profile.',
				mr: 'नक्कीच! प्रत्येक व्यवसायासाठी स्वतंत्र लिस्टिंग तयार करा.'
			}
		},
		{
			_id: 'fq5',
			question: {en: 'How do customers find my business?', mr: 'ग्राहकांना माझा व्यवसाय कसा शोधता येईल?'},
			answer: {
				en: 'Customers search by name, category, or service type. Your listing appears in search results and may be featured on the home page.',
				mr: 'ग्राहक नाव, श्रेणी किंवा सेवा प्रकारानुसार शोधतात. तुमची लिस्टिंग शोध परिणामांमध्ये दिसते.'
			}
		},
		{
			_id: 'fq6',
			question: {
				en: 'Can I edit my listing after publishing?',
				mr: 'प्रकाशित केल्यानंतर लिस्टिंग संपादित करता येईल?'
			},
			answer: {
				en: 'Yes! You can update your business details, add photos, change operating hours, and more anytime from your profile page.',
				mr: 'होय! तुम्ही तुमच्या प्रोफाइल पेजवरून कधीही तपशील अपडेट करू शकता.'
			}
		},
	],
};

// ═══════════════════════════════════════════════════════════════════════════
// ANIMATION
// ═══════════════════════════════════════════════════════════════════════════
const ease = [0.22, 1, 0.36, 1];
const fadeUp = {hidden: {opacity: 0, y: 20}, visible: {opacity: 1, y: 0, transition: {duration: 0.5, ease}}};
const stagger = {hidden: {}, visible: {transition: {staggerChildren: 0.05}}};
const popIn = {hidden: {opacity: 0, scale: 0.92}, visible: {opacity: 1, scale: 1, transition: {duration: 0.35, ease}}};

const Reveal = ({children, className = '', delay = 0}) => {
	const ref = useRef(null);
	const inView = useInView(ref, {once: true, margin: '-40px'});
	return <motion.section ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}
	                       transition={{delay}} className={className}>{children}</motion.section>;
};

const Counter = ({end, suffix = ''}) => {
	const ref = useRef(null);
	const inView = useInView(ref, {once: true});
	const [v, setV] = useState(0);
	useEffect(() => {
		if (!inView) return;
		let s = 0;
		const step = end / 90;
		const id = setInterval(() => {
			s += step;
			if (s >= end) {
				setV(end);
				clearInterval(id);
			} else setV(Math.floor(s));
		}, 16);
		return () => clearInterval(id);
	}, [inView, end]);
	return <span ref={ref}>{v.toLocaleString()}{suffix}</span>;
};

const LeafDecor = ({className = '', style = {}}) => (
	<svg viewBox="0 0 40 40" fill="none" className={className} style={style}>
		<path d="M20 4C20 4 8 14 8 24C8 30.627 13.373 36 20 36C26.627 36 32 30.627 32 24C32 14 20 4 20 4Z"
		      fill="currentColor" opacity="0.12"/>
		<path d="M20 8C20 8 12 16 12 23C12 27.418 15.582 31 20 31C24.418 31 28 27.418 28 23C28 16 20 8 20 8Z"
		      fill="currentColor" opacity="0.08"/>
	</svg>
);

// Glow effect — follows mouse, visible on light backgrounds
const GlowCard = ({children, className = '', color = 'rgba(234,88,12,0.15)'}) => {
	const mx = useMotionValue(-100);
	const my = useMotionValue(-100);
	const bg = useMotionTemplate`radial-gradient(300px circle at ${mx}px ${my}px, ${color}, transparent 80%)`;
	return (
		<div onMouseMove={e => {
			const {left, top} = e.currentTarget.getBoundingClientRect();
			mx.set(e.clientX - left);
			my.set(e.clientY - top);
		}} onMouseLeave={() => {
			mx.set(-100);
			my.set(-100);
		}} className={`relative group ${className}`}>
			<motion.div
				className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-0"
				style={{background: bg}}/>
			<div className="relative z-[1]">{children}</div>
		</div>
	);
};

// ═══════════════════════════════════════════════════════════════════════════
// ENTITY CARD
// ═══════════════════════════════════════════════════════════════════════════
const EntityCard = ({entity, onClick, badge, t: tr}) => {
	const ck = entity.categories?.[0] || 'OTHER';
	const cm = CATS[ck] || CATS.OTHER;
	const CI = cm.icon;
	const logo = entity.photos?.logo?.url;
	const FB = '/other_imgs/logo-joglekar kul-72x72.png';
	const avg = entity.rating?.average || 0;
	const cnt = entity.rating?.count || 0;
	const loc = entity.isPhysical ? entity.location : entity.serviceArea;
	const vf = entity.verification?.isVerified;

	return (
		<GlowCard color={`${cm.color}18`}>
			<button onClick={onClick} type="button"
			        className="w-full rounded-xl bg-white text-left focus:outline-none focus:ring-2 focus:ring-orange-200 relative overflow-hidden flex group/card transition-shadow duration-300 hover:shadow-md"
			        style={{border: '1px solid #EAEAEA'}}>
				<div className="w-1.5 shrink-0 rounded-l-xl"
				     style={{background: `linear-gradient(180deg, ${cm.color}, ${cm.color}55)`}}/>
				{badge && <span
					className="absolute top-3 right-3 font-custom-bold text-[0.55rem] uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-orange-500 text-white shadow-sm z-10">{badge}</span>}
				<div className="flex-1 p-4 sm:p-5 flex gap-4 items-start">
					<div
						className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden shrink-0 flex items-center justify-center"
						style={{background: cm.bg, border: `1.5px solid ${cm.color}20`}}>
						<img src={logo || FB} alt=""
						     className={`w-full h-full ${logo ? 'object-cover' : 'object-contain p-2'}`}/>
					</div>
					<div className="flex-1 min-w-0">
						<div className="flex items-center gap-1.5 mb-1">
							<h3 className="font-custom-extra-bold text-[0.92rem] sm:text-[1rem] text-gray-900 truncate leading-tight group-hover/card:text-orange-600 transition-colors"
							    title={entity.entityName}>{entity.entityName}</h3>
							{vf && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0"/>}
						</div>
						<div className="flex items-center gap-1.5 mb-2">
							<CI className="w-3.5 h-3.5 shrink-0" style={{color: cm.color}}/>
							<span className="font-custom-semi-bold text-[0.62rem]"
							      style={{color: cm.color}}>{tr(`YELLOW_PAGE.CATEGORIES.${ck}`)}</span>
						</div>
						<p className="font-custom-medium text-[0.72rem] text-gray-500 line-clamp-2 leading-relaxed mb-2.5">{entity.tagline}</p>
						<div className="flex items-center gap-4 flex-wrap">
							{avg > 0 && <div className="flex items-center gap-1"><Star
								className="w-3.5 h-3.5 text-amber-400 fill-current"/><span
								className="font-custom-bold text-[0.65rem] text-gray-700">{avg.toFixed(1)}</span><span
								className="font-custom-medium text-[0.58rem] text-gray-400">({cnt})</span></div>}
							{loc && <div className="flex items-center gap-1 text-gray-400">{entity.isPhysical ?
								<MapPin className="w-3.5 h-3.5 shrink-0"/> :
								<Globe className="w-3.5 h-3.5 shrink-0"/>}<span
								className="font-custom-medium text-[0.65rem] truncate max-w-[9rem]">{loc}</span></div>}
						</div>
					</div>
				</div>
			</button>
		</GlowCard>
	);
};

// ═══════════════════════════════════════════════════════════════════════════
// FAQ CARD
// ═══════════════════════════════════════════════════════════════════════════
const FaqCard = ({faq, index}) => {
	const Icon = FAQ_ICONS[index % FAQ_ICONS.length];
	const color = FAQ_COLORS[index % FAQ_COLORS.length];
	return (
		<GlowCard color={`${color}15`}>
			<motion.div variants={popIn}
			            className="bg-white rounded-xl p-5 sm:p-6 h-full transition-shadow duration-300 hover:shadow-md"
			            style={{border: '1px solid #EAEAEA'}}>
				<div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
				     style={{background: `${color}10`, border: `1.5px solid ${color}15`}}>
					<Icon className="w-5 h-5" style={{color}} strokeWidth={1.8}/>
				</div>
				<h4 className="font-custom-bold text-[0.88rem] text-gray-900 mb-2 leading-snug">{faq.question}</h4>
				<p className="font-custom-medium text-[0.76rem] text-gray-500 leading-relaxed">{faq.answer}</p>
			</motion.div>
		</GlowCard>
	);
};

// ═══════════════════════════════════════════════════════════════════════════
// TESTIMONIAL MODAL
// ═══════════════════════════════════════════════════════════════════════════
const TestimonialModal = ({isOpen, onClose, t: tr}) => {
	const [form, setForm] = useState({quote: '', rating: 5});
	const [sub, setSub] = useState(false);
	const [done, setDone] = useState(false);
	const go = async () => {
		if (!form.quote.trim()) return;
		setSub(true);
		try {
			await new Promise(r => setTimeout(r, 800));
			setDone(true);
			setTimeout(() => {
				onClose();
				setDone(false);
				setForm({quote: '', rating: 5});
			}, 2000);
		} catch (e) {
			console.error(e);
		} finally {
			setSub(false);
		}
	};
	if (!isOpen) return null;
	return (
		<motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}}
		            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-3"
		            onClick={onClose}>
			<motion.div initial={{opacity: 0, y: 40, scale: 0.96}} animate={{opacity: 1, y: 0, scale: 1}}
			            exit={{opacity: 0, y: 40}} transition={{duration: 0.3, ease}} onClick={e => e.stopPropagation()}
			            className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
				<div className="flex items-center justify-between p-4 border-b border-gray-100">
					<div><h3
						className="font-custom-extra-bold text-base text-gray-900">{tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.TITLE')}</h3>
						<p className="font-custom-medium text-xs text-gray-400 mt-0.5">{tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.SUBTITLE')}</p>
					</div>
					<button onClick={onClose}
					        className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100"><X
						className="w-4 h-4 text-gray-500"/></button>
				</div>
				{done ? (<motion.div initial={{scale: 0.9}} animate={{scale: 1}} className="p-8 text-center">
					<div
						className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
						<CheckCircle2 className="w-6 h-6 text-emerald-600"/></div>
					<p className="font-custom-bold text-gray-900 mb-1">{tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.SUCCESS_TITLE')}</p>
					<p className="font-custom-medium text-sm text-gray-500">{tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.SUCCESS_DESC')}</p>
				</motion.div>) : (
					<div className="p-4"><label
						className="block font-custom-bold text-sm text-gray-700 mb-1.5">{tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.RATING_LABEL')}</label>
						<div className="flex gap-1 mb-4">{[1, 2, 3, 4, 5].map(s => <button key={s} type="button"
						                                                                   onClick={() => setForm(f => ({
							                                                                   ...f,
							                                                                   rating: s
						                                                                   }))}
						                                                                   className="p-0.5 hover:scale-110 transition-transform">
							<Star
								className={`w-6 h-6 ${s <= form.rating ? 'text-amber-400 fill-current' : 'text-gray-200'}`}/>
						</button>)}</div>
						<label
							className="block font-custom-bold text-sm text-gray-700 mb-1.5">{tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.QUOTE_LABEL')}</label><textarea
							value={form.quote} onChange={e => setForm(f => ({...f, quote: e.target.value}))}
							maxLength={300} rows={3}
							placeholder={tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.QUOTE_PLACEHOLDER')}
							className="w-full px-3 py-2.5 rounded-xl border border-gray-200 font-custom-medium text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"/>
						<p className="text-right font-custom-medium text-[0.6rem] text-gray-400 mt-1">{form.quote.length}/300</p>
						<button onClick={go} disabled={!form.quote.trim() || sub}
						        className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-xl font-custom-bold text-sm text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50 active:scale-[0.98] transition-all">{sub ?
							<span
								className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> : <>
								<Send
									className="w-4 h-4"/>{tr('YELLOW_PAGE.LANDING_PAGE.TESTIMONIAL_MODAL.SUBMIT')}</>}</button>
					</div>)}
			</motion.div>
		</motion.div>
	);
};

// ═══════════════════════════════════════════════════════════════════════════
// SCROLL ARROW PAIR
// ═══════════════════════════════════════════════════════════════════════════
const ScrollArrows = ({onLeft, onRight}) => (
	<div className="flex gap-1.5">
		<button onClick={onLeft}
		        className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-orange-300 hover:bg-orange-50 transition-all shadow-sm active:scale-90">
			<ChevronLeft className="w-3.5 h-3.5 text-gray-600"/></button>
		<button onClick={onRight}
		        className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:border-orange-300 hover:bg-orange-50 transition-all shadow-sm active:scale-90">
			<ChevronRight className="w-3.5 h-3.5 text-gray-600"/></button>
	</div>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════
const YellowPagesHomePage = () => {
	const nav = useNavigate();
	const {t, i18n} = useTranslation();
	const {isAuthenticated} = useAuth();
	const lang = i18n.language?.startsWith('mr') ? 'mr' : 'en';
	const l = o => o?.[lang] || o?.en || '';

	const [q, setQ] = useState('');
	const [showTM, setShowTM] = useState(false);
	const [loading, setLoading] = useState(true);
	const [err, setErr] = useState(null);

	const [cfg, setCfg] = useState(null);
	const [stats, setStats] = useState(null);
	const [categories, setCategories] = useState([]);
	const [featured, setFeatured] = useState([]);
	const [newest, setNewest] = useState([]);
	const [testimonials, setTestimonials] = useState([]);
	const [faqs, setFaqs] = useState([]);

	const featRef = useRef(null);
	const newRef = useRef(null);

	const secMap = {};
	(cfg?.sections || []).forEach(s => {
		secMap[s.key] = s;
	});
	const vis = k => secMap[k]?.visible !== false;

	useEffect(() => {
		document.title = t('YELLOW_PAGE.LANDING_PAGE.SEO.TITLE');
	}, [t]);

	useEffect(() => {
		let c = false;
		(async () => {
			setLoading(true);
			try {
				await new Promise(r => setTimeout(r, 400));
				if (c) return;
				setCfg(D.config);
				setStats(D.stats);
				setCategories(D.categories);
				setFeatured(D.featured);
				setNewest(D.newest);
				setTestimonials(D.testimonials);
				setFaqs(D.faqs);
			} catch (e) {
				if (!c) setErr(e.message);
			} finally {
				if (!c) setLoading(false);
			}
		})();
		return () => {
			c = true;
		};
	}, []);

	const doSearch = useCallback(() => {
		const s = q.trim();
		nav(s ? `/yellow-pages/search?q=${encodeURIComponent(s)}` : '/yellow-pages/search');
	}, [nav, q]);
	const scroll = (ref, dir) => ref.current?.scrollBy({left: dir === 'left' ? -380 : 380, behavior: 'smooth'});

	const hero = cfg?.hero;
	const spot = cfg?.spotlight;
	const fCta = cfg?.footerCta;

	if (err) return (<div className="flex flex-col items-center justify-center py-16"><p
		className="font-custom-bold text-gray-600 mb-3">{t('CONSTANTS.SOMETHING_WENT_WRONG')}</p>
		<button onClick={() => window.location.reload()}
		        className="px-5 py-2 rounded-xl bg-orange-500 text-white font-custom-bold text-sm">{t('YELLOW_PAGE.LANDING_PAGE.RETRY')}</button>
	</div>);

	return (
		<>
			<main className="w-full" role="main">

				{/* ═══ HERO ═══ */}
				{vis('hero') && (
					<motion.section initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.7}}
					                className="relative min-h-[50dvh] sm:min-h-[80dvh] rounded-2xl overflow-hidden flex flex-col items-center justify-center text-center p-5 sm:p-8"
					                style={{
						                backgroundImage: `linear-gradient(to bottom,rgba(0,0,0,0.15) 0%,rgba(194,65,12,0.1) 50%,rgba(0,0,0,0.55) 100%),url("${hero?.backgroundImageUrl || ''}")`,
						                backgroundSize: 'cover',
						                backgroundPosition: 'left'
					                }}>
						{stats?.entityCount > 0 && (
							<motion.div initial={{opacity: 0, y: -8}} animate={{opacity: 1, y: 0}}
							            transition={{delay: 0.4}}
							            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full mb-4">
								<Sparkles className="w-3.5 h-3.5 text-amber-300"/><span
								className="text-white/90 text-xs sm:text-sm font-custom-semi-bold">{stats.entityCount}+ {t('YELLOW_PAGE.LANDING_PAGE.HERO_SECTION.BADGE_SUFFIX')}</span>
							</motion.div>)}
						<motion.div className="max-w-2xl" initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}}
						            transition={{delay: 0.25, duration: 0.6, ease}}>
							<h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-custom-black text-white drop-shadow-lg mb-2 leading-tight">{l(hero?.heading) || t('YELLOW_PAGE.LANDING_PAGE.HERO_SECTION.MAIN_HEADING')}</h1>
							<p className="text-white/75 font-custom-medium text-xs sm:text-sm md:text-base mb-5 max-w-lg mx-auto">{l(hero?.subHeading) || t('YELLOW_PAGE.LANDING_PAGE.HERO_SECTION.SUB_HEADING')}</p>
							<motion.div className="flex flex-col sm:flex-row gap-2 max-w-lg mx-auto"
							            initial={{opacity: 0, y: 12}} animate={{opacity: 1, y: 0}}
							            transition={{delay: 0.5}} role="search">
								<div className="relative flex-grow group"><Search
									className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-orange-500 transition-colors"/><input
									type="search" value={q} onChange={e => setQ(e.target.value)}
									onKeyDown={e => e.key === 'Enter' && doSearch()}
									className="w-full h-11 pl-10 pr-3 rounded-full font-custom-medium bg-white/95 backdrop-blur-sm border-2 border-white/20 focus:border-orange-400 focus:outline-none shadow-lg text-gray-800 placeholder-gray-400 text-sm transition-all"
									placeholder={t('YELLOW_PAGE.LANDING_PAGE.HERO_SECTION.SEARCH_PLACEHOLDER')}/></div>
								<motion.button onClick={doSearch} whileTap={{scale: 0.97}}
								               className="h-11 px-6 bg-orange-500 text-white rounded-full text-sm font-custom-bold hover:bg-orange-600 shadow-lg transition-colors">{t('YELLOW_PAGE.LANDING_PAGE.HERO_SECTION.SEARCH_BUTTON')}</motion.button>
							</motion.div>
						</motion.div>
					</motion.section>
				)}

				{/* ═══ STATS ═══ */}
				{vis('stats') && stats && (
					<Reveal className="max-w-6xl mx-auto py-5">
						<div className="flex items-center justify-center gap-5 sm:gap-8 md:gap-10 flex-wrap">
							{[{v: stats.entityCount, lk: 'STAT_BUSINESSES_LABEL', ic: Building2}, {
								v: stats.memberCount,
								lk: 'STAT_MEMBERS_LABEL',
								ic: Users
							}, {
								v: stats.categoryCount,
								lk: 'STAT_CATEGORIES_LABEL',
								ic: TrendingUp
							}].map((s, i) => s.v > 0 && (
								<div key={i} className="flex items-center gap-2.5">
									<div
										className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center border border-orange-100">
										<s.ic className="w-5 h-5 text-orange-500"/>
									</div>
									<div><p className="font-custom-extra-bold text-lg text-gray-900 leading-none">
										<Counter end={s.v} suffix="+"/></p><p
										className="font-custom-medium text-[0.65rem] text-gray-500">{t(`YELLOW_PAGE.LANDING_PAGE.STATS.${s.lk}`)}</p>
									</div>
								</div>
							))}
						</div>
					</Reveal>
				)}

				{/* ═══ CATEGORIES ═══ */}
				{vis('categories') && categories.length > 0 && (
					<Reveal className="max-w-6xl mx-auto py-5 md:py-7">
						<h2 className="text-xl sm:text-2xl font-custom-bold mb-5 text-center text-gray-900">{t('YELLOW_PAGE.LANDING_PAGE.CATEGORIES.SECTION_TITLE')}</h2>
						<motion.div className="grid grid-cols-4 md:grid-cols-8 gap-3 sm:gap-5" variants={stagger}
						            initial="hidden" whileInView="visible" viewport={{once: true}}>
							{categories.map(ci => {
								const cm = CATS[ci.slug] || CATS.OTHER;
								const CIcon = cm.icon;
								return (
									<motion.button key={ci._id} variants={popIn}
									               onClick={() => nav(`/yellow-pages/search?categories=${ci.slug}`)}
									               whileHover={{scale: 1.08, y: -2}} whileTap={{scale: 0.95}}
									               className="flex flex-col items-center gap-2.5 group">
										<div
											className="w-13 h-13 sm:w-15 sm:h-15 rounded-full flex items-center justify-center shadow-sm group-hover:shadow-md transition-all"
											style={{background: cm.bg, border: `1.5px solid ${cm.color}18`}}><CIcon
											className="w-5 h-5 sm:w-6 sm:h-6" style={{color: cm.color}}
											strokeWidth={1.8}/></div>
										<p className="text-[0.6rem] sm:text-[0.7rem] font-custom-semi-bold text-center text-gray-700 group-hover:text-orange-600 transition-colors leading-tight">{ci.name?.[lang] || ci.name?.en}</p>
									</motion.button>
								);
							})}
						</motion.div>
					</Reveal>
				)}

				{/* ═══ FEATURED — aligned with header ═══ */}
				{vis('featured') && featured.length > 0 && (
					<Reveal className="py-5 md:py-7">
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-xl sm:text-2xl font-custom-bold text-gray-900">{t('YELLOW_PAGE.LANDING_PAGE.FEATURED_BUSINESSES.SECTION_TITLE')}</h2>
							<ScrollArrows onLeft={() => scroll(featRef, 'left')}
							              onRight={() => scroll(featRef, 'right')}/>
						</div>
						<div ref={featRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 pt-1"
						     style={{scrollbarWidth: 'none'}}>
							{featured.map(e => (
								<div key={e._id}
								     className="snap-start shrink-0 w-[82vw] sm:w-[46vw] md:w-[calc(33.33%-1rem)] min-w-[290px]">
									<EntityCard entity={e} t={t} onClick={() => nav(`/yellow-pages/${e._id}`)}/>
								</div>
							))}
							<div
								className="snap-start shrink-0 w-[82vw] sm:w-[46vw] md:w-[calc(33.33%-1rem)] min-w-[290px]">
								<button onClick={() => nav('/yellow-pages/search?sortBy=rating')}
								        className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all w-full h-full min-h-[9rem] active:scale-95">
									<ArrowRight className="w-5 h-5 text-gray-300"/><span
									className="font-custom-bold text-xs text-gray-400">{t('YELLOW_PAGE.LANDING_PAGE.FEATURED_BUSINESSES.VIEW_ALL')}</span>
								</button>
							</div>
						</div>
					</Reveal>
				)}

				{/* ═══ SPOTLIGHT ═══ */}
				{vis('spotlight') && spot && (
					<Reveal className="max-w-6xl mx-auto py-5 md:py-8">
						<h2 className="text-xl sm:text-2xl font-custom-bold mb-5 text-center text-gray-900">{t('YELLOW_PAGE.LANDING_PAGE.BUSINESS_SPOTLIGHT.SECTION_TITLE')}</h2>
						<div className="rounded-2xl overflow-hidden" style={{boxShadow: '0 8px 32px rgba(0,0,0,0.07)'}}>
							<div className="flex flex-col lg:flex-row">
								<div
									className="lg:w-[45%] bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 p-6 sm:p-8 flex flex-col justify-center relative overflow-hidden">
									<Quote className="absolute top-3 left-3 w-20 h-20 text-white/8 rotate-180"/><Quote
									className="absolute bottom-3 right-3 w-14 h-14 text-white/6"/>
									<LeafDecor className="absolute -top-2 -right-4 w-20 h-20 text-white"
									           style={{transform: 'rotate(45deg)'}}/><LeafDecor
									className="absolute -bottom-3 -left-3 w-16 h-16 text-white"
									style={{transform: 'rotate(-30deg)'}}/><LeafDecor
									className="absolute top-1/2 -right-2 w-12 h-12 text-white"
									style={{transform: 'rotate(120deg)'}}/>
									<motion.div initial={{opacity: 0, x: -16}} whileInView={{opacity: 1, x: 0}}
									            viewport={{once: true}} transition={{delay: 0.2}}>
										<div
											className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm border border-white/25 px-2.5 py-1 rounded-full mb-4">
											<span
												className="text-white text-[0.65rem] font-custom-bold uppercase tracking-wider">{t('YELLOW_PAGE.LANDING_PAGE.BUSINESS_SPOTLIGHT.SPOTLIGHT_BADGE')}</span>
										</div>
										<p className="text-white font-custom-medium text-sm sm:text-base italic leading-relaxed mb-5 relative z-10">"{l(spot.quote)}"</p>
										<div className="flex items-center gap-2.5 relative z-10">
											<div
												className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center font-custom-extra-bold text-white text-base">{spot.ownerName?.charAt(0)}</div>
											<div><p className="text-white font-custom-bold text-sm">{spot.ownerName}</p>
												<p className="text-white/70 font-custom-medium text-xs">{l(spot.ownerRole)}</p>
											</div>
										</div>
									</motion.div>
								</div>
								<div className="lg:w-[55%] bg-white p-6 sm:p-8 flex flex-col justify-center">
									<motion.div initial={{opacity: 0, x: 16}} whileInView={{opacity: 1, x: 0}}
									            viewport={{once: true}} transition={{delay: 0.3}}>
										<div className="flex gap-2.5 mb-5">{(spot.stats || []).map((s, i) => (
											<div key={i}
											     className="flex-1 text-center py-3 rounded-xl bg-orange-50 border border-orange-100">
												<p className="text-lg font-custom-extra-bold text-orange-600">{s.value}</p>
												<p className="text-[0.6rem] font-custom-medium text-gray-500 mt-0.5">{l(s.label)}</p>
											</div>))}</div>
										<p className="text-gray-600 font-custom-medium text-sm leading-relaxed mb-5">{l(spot.story)}</p>
										<button
											className="self-start bg-orange-500 text-white font-custom-bold py-2 px-5 rounded-xl hover:bg-orange-600 shadow-md inline-flex items-center gap-2 text-sm transition-colors active:scale-95">{t('YELLOW_PAGE.LANDING_PAGE.BUSINESS_SPOTLIGHT.READ_MORE_BUTTON')}
											<ArrowRight className="w-4 h-4"/></button>
									</motion.div>
								</div>
							</div>
						</div>
					</Reveal>
				)}

				{/* ═══ NEWEST — aligned with header ═══ */}
				{vis('newest') && newest.length > 0 && (
					<Reveal className="py-5 md:py-7">
						<div className="flex items-center justify-between mb-4">
							<h2 className="text-xl sm:text-2xl font-custom-bold text-gray-900">{t('YELLOW_PAGE.LANDING_PAGE.NEW_DIRECTORY.SECTION_TITLE')}</h2>
							<ScrollArrows onLeft={() => scroll(newRef, 'left')}
							              onRight={() => scroll(newRef, 'right')}/>
						</div>
						<div ref={newRef} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 pt-1"
						     style={{scrollbarWidth: 'none'}}>
							{newest.map(e => (
								<div key={e._id}
								     className="snap-start shrink-0 w-[82vw] sm:w-[46vw] md:w-[calc(33.33%-1rem)] min-w-[290px]">
									<EntityCard entity={e} t={t}
									            badge={t('YELLOW_PAGE.LANDING_PAGE.NEW_DIRECTORY.NEW_BADGE')}
									            onClick={() => nav(`/yellow-pages/${e._id}`)}/>
								</div>
							))}
						</div>
					</Reveal>
				)}

				{/* ═══ TESTIMONIALS — centered layout ═══ */}
				{vis('testimonials') && testimonials.length > 0 && (
					<Reveal className="max-w-6xl mx-auto py-5 md:py-8">
						<h2 className="text-xl sm:text-2xl font-custom-bold text-gray-900 text-center mb-5">{t('YELLOW_PAGE.LANDING_PAGE.TESTIMONIALS.SECTION_TITLE')}</h2>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							{testimonials.map((ts, i) => (
								<GlowCard key={ts._id}>
									<motion.div initial={{opacity: 0, y: 16}} whileInView={{opacity: 1, y: 0}}
									            viewport={{once: true}}
									            transition={{delay: i * 0.1, duration: 0.4, ease}}
									            className="bg-white rounded-xl overflow-hidden h-full transition-shadow duration-300 hover:shadow-md"
									            style={{border: '1px solid #EAEAEA'}}>
										<div className="bg-gradient-to-b from-orange-50/60 to-white px-5 pt-5 pb-3">
											<Quote className="w-6 h-6 text-orange-300 mb-2"/>
											<p className="font-custom-medium text-[0.82rem] text-gray-700 leading-relaxed">"{ts.quote}"</p>
										</div>
										<div
											className="px-5 pb-4 pt-2 flex items-center justify-between border-t border-gray-100">
											<div className="flex items-center gap-2.5">
												<div
													className="w-9 h-9 rounded-full flex items-center justify-center font-custom-extra-bold text-xs"
													style={{
														background: '#FFF7ED',
														color: '#EA580C',
														border: '2px solid #FED7AA'
													}}>{ts.author?.charAt(0)}</div>
												<div><p
													className="font-custom-bold text-gray-900 text-[0.78rem]">{ts.author}</p>
													<p className="font-custom-medium text-[0.62rem] text-gray-400">{ts.authorTitle}</p>
												</div>
											</div>
											<div className="flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j}
											                                                                 className={`w-3 h-3 ${j < ts.rating ? 'text-amber-400 fill-current' : 'text-gray-200'}`}/>)}</div>
										</div>
									</motion.div>
								</GlowCard>
							))}
						</div>
						{isAuthenticated && (
							<div className="flex justify-center mt-5">
								<button onClick={() => setShowTM(true)}
								        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-custom-bold text-orange-600 bg-orange-50 border border-orange-200 hover:bg-orange-100 transition-all active:scale-95">
									<Send
										className="w-3.5 h-3.5"/>{t('YELLOW_PAGE.LANDING_PAGE.TESTIMONIALS.SHARE_CTA')}
								</button>
							</div>
						)}
					</Reveal>
				)}

				{/* ═══ FAQ — 3x2 grid ═══ */}
				{vis('faq') && faqs.length > 0 && (
					<Reveal className="max-w-6xl mx-auto py-5 md:py-8">
						<h2 className="text-xl sm:text-2xl font-custom-bold mb-1.5 text-center text-gray-900">{t('YELLOW_PAGE.LANDING_PAGE.FAQ.SECTION_TITLE')}</h2>
						<p className="text-gray-500 font-custom-medium text-xs sm:text-sm text-center mb-5 max-w-md mx-auto">{t('YELLOW_PAGE.LANDING_PAGE.FAQ.SECTION_SUBTITLE')}</p>
						<motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" variants={stagger}
						            initial="hidden" whileInView="visible" viewport={{once: true}}>
							{faqs.map((faq, idx) => <FaqCard key={faq._id}
							                                 faq={{question: l(faq.question), answer: l(faq.answer)}}
							                                 index={idx}/>)}
						</motion.div>
						<p className="text-center mt-5 font-custom-medium text-xs text-gray-400">{t('YELLOW_PAGE.LANDING_PAGE.FAQ.FAQ_STILL_QUESTIONS', {defaultValue: lang === 'mr' ? 'अजून प्रश्न आहेत?' : 'Still have questions?'})}{' '}
							<button
								className="text-orange-500 hover:text-orange-600 font-custom-bold underline underline-offset-2 inline-flex items-center gap-1">
								<HelpCircle
									className="w-3 h-3"/>{t('YELLOW_PAGE.LANDING_PAGE.FAQ.FAQ_GET_HELP', {defaultValue: lang === 'mr' ? 'मदत घ्या' : 'Get Help'})}
							</button>
						</p>
					</Reveal>
				)}

				{/* ═══ FOOTER CTA — prominent, full width ═══ */}
				{vis('footerCta') && fCta && (
					<Reveal className="py-6 md:py-10">
						<div
							className="bg-gradient-to-br from-orange-50 via-amber-50/40 to-orange-50 rounded-2xl py-12 sm:py-16 px-6 text-center border border-orange-100/50 relative overflow-hidden"
							style={{boxShadow: '0 4px 20px rgba(234,88,12,0.05)'}}>
							<LeafDecor className="absolute -top-4 -left-4 w-24 h-24 text-orange-300"
							           style={{transform: 'rotate(-20deg)'}}/><LeafDecor
							className="absolute -bottom-4 -right-4 w-20 h-20 text-orange-300"
							style={{transform: 'rotate(160deg)'}}/><LeafDecor
							className="absolute top-1/2 -right-6 w-16 h-16 text-orange-200"
							style={{transform: 'rotate(80deg)'}}/>
							<div className="relative z-10">
								<Zap className="w-9 h-9 text-orange-400 mx-auto mb-4"/>
								<h2 className="text-2xl sm:text-3xl md:text-4xl font-custom-bold mb-3 text-gray-900">{l(fCta.heading)}</h2>
								<p className="max-w-xl mx-auto text-gray-600 mb-7 font-custom-medium text-sm sm:text-base leading-relaxed">{l(fCta.description)}</p>
								<button onClick={() => nav('/yellow-pages/onboard')}
								        className="bg-orange-500 text-white font-custom-bold py-3.5 px-10 rounded-xl hover:bg-orange-600 shadow-lg hover:shadow-xl inline-flex items-center gap-2.5 text-base transition-all active:scale-95">
									<Building2 className="w-5 h-5"/>{t('YELLOW_PAGE.LANDING_PAGE.LIST_YOUR_ENTITY')}
								</button>
							</div>
						</div>
					</Reveal>
				)}

				{/* ═══ EMPTY STATE ═══ */}
				{!loading && !featured.length && !newest.length && !testimonials.length && !faqs.length && (
					<Reveal className="py-12 text-center">
						<div className="max-w-sm mx-auto">
							<div
								className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4 border border-orange-100">
								<Building2 className="w-8 h-8 text-orange-400"/></div>
							<h3 className="font-custom-bold text-lg text-gray-900 mb-2">{t('YELLOW_PAGE.LANDING_PAGE.EMPTY_STATE.TITLE')}</h3>
							<p className="font-custom-medium text-sm text-gray-500 mb-5">{t('YELLOW_PAGE.LANDING_PAGE.EMPTY_STATE.DESCRIPTION')}</p>
							<button onClick={() => nav('/yellow-pages/onboard')}
							        className="bg-orange-500 text-white font-custom-bold py-2.5 px-5 rounded-xl hover:bg-orange-600 shadow-md inline-flex items-center gap-2 text-sm">
								<Building2 className="w-4 h-4"/>{t('YELLOW_PAGE.LANDING_PAGE.EMPTY_STATE.CTA')}</button>
						</div>
					</Reveal>
				)}
			</main>
			<TestimonialModal isOpen={showTM} onClose={() => setShowTM(false)} t={t}/>
		</>
	);
};

export default YellowPagesHomePage;